import { Component, NgZone, OnInit, inject, signal } from '@angular/core';
import { Params, Router } from '@angular/router';
import { App } from '@capacitor/app';
import { Device, DeviceId } from '@capacitor/device';
import { LoadingController, ToastController } from '@ionic/angular/standalone';
import { Store } from '@ngrx/store';
import { clone, get } from 'lodash';
import { DateTime } from 'luxon';
import { LocalStorageService } from 'ngx-webstorage';
import OneSignal, {
  LogLevel,
  NotificationWillDisplayEvent
} from 'onesignal-cordova-plugin';
import { combineLatest, concatMap, delay, of, tap } from 'rxjs';
import { Logger } from '@app/utils/logger';
import { AnalyticActions } from '@components/analytic/analytic.actions';
import { EVENT_TYPE } from '@components/analytic/analytic.enum';
import { HeartbeatActions } from '@components/heartbeat/heartbeat.actions';
import { environment } from '@environments/environment';
import { EhrOrderAndEhrLabType, ReportStatus } from '@interfaces/schema';
import { assistantChatActions } from '@pages/assistant/chat/chat.actions';
import { MediaLibraryViewQueryParams } from '@pages/media-library/view/view.interface';
import { nutritionGuideActions } from '@pages/report/nourish/nutrition-guide/nutrition-guide.action';
import { EVENT } from '@providers/event/event.enum';
import { EventService } from '@providers/event/event.service';
import { Session } from '@providers/session/session.service';
import {
  OneSignalActions,
  SaveNotificationDevicePayload
} from './one-signal.action';
import { ROUTING } from './one-signal.config';
import { OneSignalService } from './one-signal.service';

type Tag = 'connectedAt' | 'id';

/**
 * One Signal Component.
 *
 * @exports
 * @class OneSignalComponent
 * @implements {OnInit}
 */
@Component({
  selector: 'app-one-signal',
  template: ``,
  standalone: true,
  providers: [OneSignalService]
})
export class OneSignalComponent implements OnInit {
  private readonly events = inject(EventService);
  private readonly loadingController = inject(LoadingController);
  private readonly router = inject(Router);
  private readonly service = inject(OneSignalService);
  private readonly session = inject(Session);
  private readonly storage = inject(LocalStorageService);
  private readonly store = inject(Store);
  private readonly toastController = inject(ToastController);
  private readonly zone = inject(NgZone);

  private readonly token = signal<string | null>(null);

  private readonly logger = Logger.getInstance(`OneSignal`);

  /**
   * Setup.
   *
   * @memberof OneSignalComponent
   */
  ngOnInit(): void {
    // Initialize on device ready
    document.addEventListener(
      'deviceready',
      () => {
        // Initialize OneSignal
        this.initialize();
        // Setup subscriptions
        this.subscribes();
      },
      false
    );
  }

  /**
   * Subscribes to router and session changes.
   *
   * @memberof OneSignalComponent
   */
  subscribes(): void {
    // Track token changes
    this.storage
      .observe(this.session.keys.token)
      .pipe(
        tap(() => {
          this.logger.info(`Token changed, update OneSignal subscription...`);
        }),
        tap(() => {
          this.logger.info(`Clearing existing OneSignal tags...`);
          // Remove existing tags
          OneSignal.User.removeTags(['id', 'connectedAt'] as Array<Tag>);
        }),
        tap((token: string) => {
          // Copy current token value
          const currentToken = clone(this.token());
          if (!token || token !== currentToken) {
            if (token) {
              /**
               * If token exists but different,
               * it means user logged in with another account or switch to different profile,
               * we should unsubscribe from OneSignal to avoid receiving notifications for previous user.
               */
              this.logger.info(
                `Token changed, unsubscribing from OneSignal for previous user`
              );
            } else {
              /**
               * If token is cleared, it means user logged out,
               * we should also unsubscribe from OneSignal to avoid receiving notifications after logout.
               * In this case, we will also clear all notifications to avoid showing irrelevant notifications after logout.
               */
              this.logger.info(
                `Token not found, unsubscribing from OneSignal for previous user`
              );
              // Clear all notifications
              OneSignal.Notifications.clearAll();
            }
            if (currentToken) {
              // Unsubscribe from OneSignal
              this.updateNotificationDevice({
                context: {
                  // Use previous token for unsubscribing since current token is already changed
                  token: currentToken
                },
                form: {
                  externalId: null,
                  onesignalId: null,
                  pushSubscriptionId: null
                } as any
              });
            }
          }
        }),
        delay(2500)
      )
      .subscribe((token) => {
        // Update token value
        this.token.set(token);
        // Reconnect to OneSignal
        this.connect();
      });
  }

  /**
   * Reconnect to OneSignal with updated tags.
   *
   * @memberof OneSignalComponent
   */
  connect(): void {
    // Get profile from session token
    const profile = this.session.get();
    const profileId = profile?.id ?? profile?.username;
    const externalId: Array<string> = [];
    // Connect to OneSignal
    of(true)
      .pipe(
        tap(() => {
          this.logger.info('Connecting to OneSignal...');
        }),
        concatMap(() => Device.getId()),
        tap((deviceInfo) => externalId.push(deviceInfo.identifier)),
        tap(() => {
          if (profileId) {
            // If authenticated, include profile ID in external ID
            externalId.push(profileId);
          } else {
            // If not authenticated, include current date
            const date = DateTime.now().toISODate({ format: 'basic' });
            externalId.push(date);
          }
        }),
        tap(() => {
          // Login
          OneSignal.login(externalId.join('_'));
          this.logger.info(
            `Logged in to OneSignal with External ID: ${externalId.join('_')}`
          );
        }),
        tap(() => {
          // Prepare tags
          const tags: Record<Tag, string> = {
            id: profileId ?? undefined,
            connectedAt: DateTime.now().toISO()
          };
          // Set tags
          OneSignal.User.addTags(tags);
          this.logger.info('Set OneSignal tags:', JSON.stringify(tags));
        })
      )
      .subscribe({
        /**
         * @description Connected.
         */
        next: () => {
          this.logger.info('Connected to OneSignal successfully.');
          this.connected();
        },
        /**
         * @description Error.
         */
        error: (error: unknown) => {
          this.logger.error('Failed to connect to OneSignal:', error);
        }
      });
  }

  /**
   * @description OneSignal connected handler.
   * @memberof OneSignalComponent
   * @returns {void}
   */
  connected(): void {
    of(true)
      .pipe(
        concatMap(() =>
          combineLatest([
            OneSignal.User.getOnesignalId(),
            OneSignal.User.getExternalId(),
            OneSignal.User.pushSubscription.getIdAsync(),
            OneSignal.User.getTags()
          ])
        )
      )
      .subscribe({
        /**
         * @description Handle response data.
         * @param {[string, string, string, Record<string, string>]} param0 Data.
         * @returns {void}
         */
        next: ([onesignalId, externalId, pushSubscriptionId, tags]) => {
          this.logger.log(
            `OneSignal connected with User ID: ${onesignalId}, External ID: ${externalId}, Push Subscription ID: ${pushSubscriptionId} and Tags: ${JSON.stringify(tags)}`
          );
          // Log OneSignal connection event
          this.store.dispatch(
            HeartbeatActions.sendMessage({
              message: `OneSignal connected ${JSON.stringify({ onesignalId, externalId, pushSubscriptionId, tags })}`
            })
          );
          /**
           * Save notification device info if token exists,
           * which means user is authenticated and can receive personalized notifications.
           */
          const token = this.token();
          if (token) {
            this.updateNotificationDevice({
              form: {
                externalId,
                onesignalId,
                pushSubscriptionId
              } as any
            });
          }
        }
      });
  }

  /**
   * Handle notification visibility in foreground.
   *
   * @param {NotificationWillDisplayEvent} notification NotificationReceivedEvent.
   */
  async handleNotificationVisibility(
    notification: NotificationWillDisplayEvent
  ): Promise<void> {
    const payload: Record<string, any> =
      notification.getNotification().additionalData;

    this.logger.debug('new notification', JSON.stringify(payload));
    this.store.dispatch(
      HeartbeatActions.sendMessage({
        message: `Push notification received ${JSON.stringify(payload)}`
      })
    );

    if (payload['__typename'] === 'appointment-chat') {
      this.logger.debug('new notification for appointment-chat');

      const appState = await App.getState();

      const isOnConsultationChatPage = this.router.url.includes(
        '/consultation/appointment/chat'
      );
      // dont show the notification, if user still on consulation/chat page.
      if (appState.isActive && isOnConsultationChatPage) {
        this.logger.debug(
          'notification for appointment-chat will not show, reason = ',
          {
            appStateIsActive: appState.isActive,
            isOnConsultationChatPage
          }
        );
        notification.preventDefault();
        return;
      }
    } else if (
      // Handle avatar notifications in foreground
      payload['__typename'] === 'avatar-completed' ||
      payload['__typename'] === 'avatar-failed'
    ) {
      this.logger.debug('new notification for avatar', payload['__typename']);

      const appState = await App.getState();

      if (appState.isActive) {
        // App is in foreground, publish event and show toast
        const isCompleted = payload['__typename'] === 'avatar-completed';

        this.events.publish(EVENT.AVATAR_NOTIFICATION, {
          type: isCompleted ? 'AVATAR_COMPLETED' : 'AVATAR_FAILED',
          action: isCompleted ? 'OPEN_PROFILE' : 'RETRY_AVATAR',
          profileId: payload['profileId'],
          reason: payload['reason']
        });

        // Show toast notification with custom buttons
        if (isCompleted) {
          let autoRefreshTimeout: ReturnType<typeof setTimeout>;

          const toast = await this.toastController.create({
            message: 'Your avatar is set! Auto reload in 3 seconds.',
            duration: 3000,
            position: 'top',
            cssClass: 'avatar-success-toast',
            buttons: [
              {
                text: 'Continue',
                role: 'cancel',
                /**
                 * @description Handler.
                 */
                handler: () => {
                  clearTimeout(autoRefreshTimeout);
                  this.to('/tabs/home');
                }
              }
            ]
          });

          await toast.present();

          // Auto-refresh after 3 seconds if button not clicked
          autoRefreshTimeout = setTimeout(() => {
            this.to('/tabs/home');
          }, 3000);

          toast
            .onDidDismiss()
            .then(() => {
              clearTimeout(autoRefreshTimeout);
            })
            .catch(() => {
              // no-op
            });
        } else {
          const toast = await this.toastController.create({
            message: 'There was an issue creating your avatar.',
            duration: 3000,
            position: 'top',
            cssClass: 'avatar-error-toast',
            buttons: [
              {
                text: 'Try Again',
                role: 'cancel',
                /**
                 * @description Handler.
                 */
                handler: () => {
                  this.to('/avatar/create');
                }
              }
            ]
          });

          await toast.present();
        }

        // Don't show the system notification since we're showing a toast
        notification.preventDefault();
        return;
      }
    } else if (payload['__typename'] === 'nutrigenetic-plus') {
      const appState = await App.getState();
      this.logger.debug('new notification for nutrigenetic-plus');

      if (appState.isActive) {
        // fetch the latest progress of nourish plus generation to update the UI in case user is on the nourish plus guide page
        this.store.dispatch(
          nutritionGuideActions.fetchProcessGenerationNourishPlus({
            isWaitingProgressCompleted: true
          })
        );

        // fetch personal information to trigger the guard and redirect to report if generation is completed and for show food logging
        this.store.dispatch(nutritionGuideActions.fetchPersonalInformation());
      }
    } else if (
      payload['__typename'] === 'health-tracker-alert' ||
      payload['__typename'] === 'health-tracker-predictive'
    ) {
      const appState = await App.getState();
      this.logger.debug(
        'new notification for health tracker alert or predictive'
      );

      const isOnAsaXChatPage = this.router.url.includes(
        `/tabs/assistant?channelId=${payload['channelId']}`
      );

      if (appState.isActive && payload['channelId'] && isOnAsaXChatPage) {
        this.store.dispatch(
          assistantChatActions.setChannelChatQuery({
            query: {
              page: 0,
              order: '-createdAt',
              channelId: [payload['channelId']]
            }
          })
        );
        this.store.dispatch(
          assistantChatActions.fetchChannelChats({
            page: 0,
            reset: true
          })
        );
      }
    }
    // Display the notification as usual
    notification.getNotification().display();
  }

  /**
   * Setup onesignal.
   *
   * @memberof OneSignalComponent
   */
  initialize(): void {
    this.logger.log(`Initialize`);

    // Set log level to debug
    OneSignal.Debug.setLogLevel(LogLevel.Debug);
    // Initialize OneSignal with App ID
    OneSignal.initialize(environment.ONESIGNAL.APP_ID);

    /**
     * @description Notification opened handler.
     * @param {object} data Payload.
     * @see https://documentation.onesignal.com/docs/en/mobile-sdk-reference#addclicklistener-push
     */
    OneSignal.Notifications.addEventListener(
      'click',
      this.onNotificationOpened.bind(this)
    );
    /**
     * @description Notification visibility handler.
     * @param {NotificationWillDisplayEvent} notification NotificationReceivedEvent.
     * @see https://documentation.onesignal.com/docs/en/mobile-sdk-reference#addforegroundlifecyclelistener-push
     */
    OneSignal.Notifications.addEventListener(
      'foregroundWillDisplay',
      this.handleNotificationVisibility.bind(this)
    );
    /**
     * @description Permission change handler.
     * @param {string} permission Permission status.
     * @see https://documentation.onesignal.com/docs/en/mobile-sdk-reference#addpermissionobserver-push
     */
    OneSignal.Notifications.addEventListener(
      'permissionChange',
      (permission) => {
        this.logger.log('Permission changed:', JSON.stringify(permission));
        this.connected();
      }
    );
    /**
     * @description User change handler.
     * @param {object} user User info.
     * @see https://documentation.onesignal.com/docs/en/mobile-sdk-reference#addeventlistener-user-state
     */
    OneSignal.User.addEventListener('change', (user) => {
      this.logger.log('User changed:', JSON.stringify(user));
      this.connected();
    });
    /**
     * @description Push subscription change handler.
     * @param {boolean} isSubscribed Subscription status.
     * @see https://documentation.onesignal.com/docs/en/mobile-sdk-reference#addobserver-push-subscription-changes
     */
    OneSignal.User.pushSubscription.addEventListener(
      'change',
      (isSubscribed) => {
        this.logger.log(
          'Push subscription changed:',
          JSON.stringify(isSubscribed)
        );
        this.connected();
      }
    );

    // Prompts the user for notification permissions.
    //    * Since this shows a generic native prompt, we recommend instead using an In-App Message to prompt for notification permission (See step 7) to better communicate to your users what notifications they will get.
    of(true)
      .pipe(
        concatMap(() => OneSignal.Notifications.canRequestPermission()),
        tap((canRequestPermission) => {
          this.logger.log(
            `Can request permission notification: ${canRequestPermission}`
          );
        }),
        concatMap((canRequestPermission) => {
          if (canRequestPermission) {
            return OneSignal.Notifications.requestPermission(true);
          }
          return of(false);
        }),
        tap((accepted) => {
          this.logger.log(`User accepted notification: ${accepted}`);
        }),
        delay(2500)
      )
      .subscribe({
        /**
         * @description Error.
         */
        error: this.logger.error.bind(this.logger),
        /**
         * @description Complete.
         */
        complete: () => {
          this.logger.log('OneSignal initialization completed.');
          // After initialization, connect to OneSignal
          this.connect();
          // Set token from session if exists
          const token = this.session.token;
          if (token) {
            this.token.set(token);
          }
        }
      });
  }

  /**
   * Handle notification opened actions.
   *
   * @param {object} data Payload.
   * @memberof OneSignalComponent
   */
  onNotificationOpened(data: any): void {
    const profile = this.session.getProfile();
    const payload =
      data.notification?.payload?.additionalData ??
      data.notification?.additionalData;

    this.logger.log('Received push notif', JSON.stringify(payload));
    this.store.dispatch(
      HeartbeatActions.sendMessage({
        message: `Push notification opened ${JSON.stringify(payload)}`
      })
    );

    const id = payload?.id;
    const date = DateTime.now().toISO();
    const type = payload?.__typename ?? 'unknown';
    const profile_id = profile?.id ?? '';
    // Log analytic event
    this.store.dispatch(
      AnalyticActions.log({
        name: EVENT_TYPE.NOTIF_CLICK,
        params: { id, type, date, profile_id }
      })
    );

    // Determine route and query params based on notification type
    let route: string = ROUTING[type as keyof typeof ROUTING] || '';
    let queryParams: Params = {};
    let replaceUrl = false;

    // Handle product page notifications separately
    if (type === 'product-page') {
      this.onNotificationOpenedForProductPage(payload);
      return;
    }
    // Handle specific notification types
    if (type === 'virtual-assistant') {
      if (
        payload?.action === 'generate-daily-outlook' ||
        payload?.action === 'generate-asa-age-progress'
      ) {
        // Determine page title
        const pageTitle = payload?.action
          .replace(/^generate-?/, '') // remove "generate" at the beginning
          .replace(/-/g, '_');
        // Log analytic event
        this.store.dispatch(
          AnalyticActions.log({
            name: EVENT_TYPE.NOTIF_CLICK,
            params: {
              page_title: `notification_asax_${pageTitle}`,
              name: `Notification AsaX ${payload?.action === 'generate-daily-outlook' ? 'Daily Outlook' : 'Asa Age Progress'}`,
              profile_id,
              date
            }
          })
        );
        // Set action query param
        queryParams = { action: payload?.action ?? 'generate-daily-outlook' };
      }
    } else if (type === 'appointment-book') {
      queryParams = { id: payload.id };
    } else if (type === 'appointment-book-request') {
      queryParams = { segment: 'CONSULTATION' };
    } else if (type === 'appointment-chat') {
      route = route.replace(':queue_id', payload.queueId);
    } else if (type === 'article') {
      queryParams = { id: payload.id };
    } else if (type === 'pickup-kit-delivery') {
      route = route.replace(':id', payload.id);
      queryParams = { type: 'PurchaseProductPickup' };
    } else if (type === 'dna-report') {
      queryParams = { segment: 'DNA' };
    } else if (type === 'ehr_mcu') {
      const url =
        payload.type === EhrOrderAndEhrLabType.EhrOrder ? 'mcu' : 'view';
      const id =
        payload.type === EhrOrderAndEhrLabType.EhrOrder
          ? payload.ehrOrderId
          : payload.ehrLabId;
      // Replace :url and set id param
      route = route.replace(':url', url);
      queryParams = { id };
    } else if (type === 'order') {
      route = route.replace(':id', payload.id);
    } else if (type === 'lab-sample') {
      route = route.replace(':tracking_id', payload.trackingId);
    } else if (type === 'media-music') {
      if (payload.id) {
        route = route.concat('/view');
        queryParams = {
          id: payload.id,
          dataSource: 'music',
          mediaType: 'audio'
        } as MediaLibraryViewQueryParams;
      } else {
        queryParams = { type: 'music' };
      }
    } else if (type === 'media-podcast') {
      if (payload.id) {
        route = route.concat('/view');
        queryParams = { id: payload.id, type: 'podcast' };
      } else {
        queryParams = { type: 'podcast' };
      }
    } else if (type === 'shop') {
      queryParams = { productId: payload.id };
    } else if (type === 'shop-product') {
      // /shop/product?id=1963203b-9f9d-4486-82f3-d7e4f1fa5d5b
      if (payload.id) {
        route = route.concat('/product');
        queryParams = { id: payload.id };
      }
    } else if (type === 'asa-x') {
      if (payload.id) {
        route = route.concat('/merchant');
        queryParams = { code: payload.id, fromQr: false };
      }
    } else if (type === 'reminder-order-shipment-form') {
      queryParams = { id: payload.id };
    } else if (type === 'avatar-completed') {
      // Publish event for avatar effects to handle
      this.events.publish(EVENT.AVATAR_NOTIFICATION, {
        type: 'AVATAR_COMPLETED',
        action: 'OPEN_PROFILE',
        profileId: payload?.profileId,
        reason: undefined
      });

      if (payload.profileId && payload.profileId !== profile_id) {
        route = '/profile/notification-switch';
        queryParams = {
          redirectTo: route,
          profileId: payload?.profileId
        };
      }
    } else if (type === 'avatar-failed') {
      // Publish event for avatar effects to handle
      this.events.publish(EVENT.AVATAR_NOTIFICATION, {
        type: 'AVATAR_FAILED',
        action: 'RETRY_AVATAR',
        profileId: payload?.profileId,
        reason: payload?.reason
      });

      if (payload.profileId && payload.profileId !== profile_id) {
        route = '/profile/notification-switch';
        queryParams = {
          redirectTo: route,
          profileId: payload?.profileId
        };
      }
    } else if (type === 'daily-journal') {
      // Log analytic event
      this.store.dispatch(
        AnalyticActions.log({
          name: EVENT_TYPE.NOTIF_CLICK,
          params: {
            page_title: `notification_asax_${payload.__typename}`,
            name: `Notification AsaX Daily Journal`,
            profile_id: payload.profileId,
            date
          }
        })
      );

      if (payload.profileId && payload.profileId !== profile_id) {
        route = '/profile/notification-switch';
        queryParams = {
          redirectTo: route,
          profileId: payload.profileId
        };
      }
    } else if (type === 'wearable-health-status') {
      // Log analytic event
      this.store.dispatch(
        AnalyticActions.log({
          name: EVENT_TYPE.NOTIF_CLICK,
          params: {
            page_title: `notification_asax_${payload.__typename}`,
            name: `Notification AsaX Wearable Health Status`,
            profile_id: payload.profileId,
            date
          }
        })
      );
    } else if (type === 'asa-age-progress-not-eligible') {
      // Log analytic event
      this.store.dispatch(
        AnalyticActions.log({
          name: EVENT_TYPE.NOTIF_CLICK,
          params: {
            page_title: `notification_asax_${payload.__typename}`,
            name: `Notification Asa Age Progress Not Eligible`,
            profile_id: payload.profileId ?? '',
            date
          }
        })
      );
    } else if (
      type === 'health-tracker-alert' ||
      type === 'health-tracker-predictive'
    ) {
      // save notification id for tracking
      this.store.dispatch(
        OneSignalActions.saveNotification({
          notificationId: payload?.notification_id
        })
      );

      // Set need refetch chat to true to trigger refetch when user open the notification, since these notifications are related to chat, we want to make sure the chat is updated when user open the notification
      this.store.dispatch(
        assistantChatActions.setNeedRefetchChat({ isNeedRefetchChat: true })
      );

      if (payload.user_id && payload.user_id !== profile_id) {
        route = '/profile/notification-switch';
        queryParams = {
          channelId: payload?.channelId,
          redirectTo: route,
          profileId: payload?.user_id
        };
      } else {
        replaceUrl = true;
        queryParams = {
          channelId: payload?.channelId
        };
      }
    } else if (payload?.__typename === 'health-tracker-reminder') {
      // save notification id for tracking
      this.store.dispatch(
        OneSignalActions.saveNotification({
          notificationId: payload?.notification_id
        })
      );
    } else if (payload?.__typename === 'nutrigenetic-plus') {
      if (payload.user_id && payload.user_id !== profile_id) {
        route = '/profile/notification-switch';
        queryParams = {
          redirectTo: payload?.isFirstGenerate
            ? '/report/nourish/nutrition-plus-guide'
            : '/report/nourish/recalculate/success',
          profileId: payload?.user_id
        };
      } else {
        if (payload?.isFirstGenerate) {
          route = '/report/nourish/nutrition-plus-guide';
        }
      }
    }

    // Navigate to route with query params
    this.to(route || '/tabs/home', queryParams, replaceUrl);
  }

  /**
   * @description Open product page notification actions.
   *
   * @param {object} payload Payload.
   * @returns {Promise<void>}
   * @memberof OneSignalComponent
   */
  async onNotificationOpenedForProductPage(payload: any): Promise<void> {
    // check status connection
    if (!navigator.onLine) return;

    const loading = await this.loadingController.create({
      message: 'Loading...'
    });

    await loading.present();

    of(true)
      .pipe(concatMap(() => this.service.reportStatus()))
      .subscribe({
        /**
         * Handle response data.
         *
         * @param {ReportStatus} res Data.
         */
        next: (res: ReportStatus) => {
          loading.dismiss();

          const code = get(res as any, 'code');
          if (code) {
            if (code === 'ANCESTRY') {
              this.to('/report/ancestry');
            } else if (code === 'DNA_PREMIUM') {
              this.to('/report/premium', {
                reportType: code
              });
            } else if (code === 'PRODIGY') {
              this.to('/prodigy');
            } else if (code === 'DNA_360') {
              this.to('/report/360');
            } else if (code === 'NUTRIGENETIC') {
              this.to('/report/nourish');
            }
          } else {
            if (/ancestry/i.test(payload?.type)) {
              this.router.navigate(['report', 'ancestry', 'intro']);
            } else if (/dna_premium|premiun dna|premium/i.test(payload?.type)) {
              this.router.navigate(['report', 'premium', 'intro']);
            } else if (/prodigy/i.test(payload?.type)) {
              this.to('/prodigy/intro');
            } else if (/dna_360|360 dna/i.test(payload?.type)) {
              this.router.navigate(['report', '360', 'intro']);
            } else if (
              /nutrigenetic|nourish|nutri dna|nutri_dna/i.test(payload?.type)
            ) {
              this.to('/report/nourish/intro');
            } else {
              this.to('/report');
            }
          }
        },
        /**
         * @description Error.
         */
        error: () => {
          loading.dismiss();
        },
        /**
         * @description Complete.
         */
        complete: () => {
          loading.dismiss();
        }
      });
  }

  /**
   * Navigate to path with params.
   *
   * @param {string} path Path.
   * @param {Params} [queryParams] Params.
   * @param {boolean} [replaceUrl] Replace URL.
   * @memberof OneSignalComponent
   */
  to(path: string, queryParams: Params = {}, replaceUrl = false): void {
    this.zone.run(() => {
      this.logger.info(`Navigating to ${path} with params`, queryParams);
      this.router.navigate([path], {
        replaceUrl,
        queryParams,
        onSameUrlNavigation: 'reload'
      });
    });
  }

  /**
   * @description Update notification device with OneSignal Player ID.
   *
   * @param {SaveNotificationDevicePayload} payload Payload.
   * @memberof OneSignalComponent
   */
  updateNotificationDevice({
    form,
    context
  }: SaveNotificationDevicePayload): void {
    of(true)
      .pipe(concatMap(() => Device.getId()))
      .subscribe({
        /**
         * @description Handle device info response.
         * @param {DeviceId} deviceInfo Device info.
         */
        next: (deviceInfo: DeviceId) => {
          this.logger.info(
            `Updating notification device with Device ID: ${deviceInfo.identifier} and OneSignal ID: ${form.onesignalId}`
          );
          this.store.dispatch(
            OneSignalActions.saveNotificationDevice({
              context,
              form: {
                ...form,
                deviceId: deviceInfo.identifier
              }
            })
          );
        }
      });
  }
}
